import React, { useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, IconButton, Typography } from "@mui/material";
import SideNavBar from "../components/LawyerCompo/Dashboard/SideNavBar";
import Navbar from "../components/LawyerCompo/Dashboard/Navbar";
import {
  DashboardThemeProvider,
  useDashboardTheme,
} from "../ThemeUtils/Dashboard/DashboardThemeContext";
import ChatBotFabAnimated, {
  ChatBox,
} from "../components/AiBotUtils/ChatBotFabAnimated";
import { CloseRounded } from "@mui/icons-material";
import { ChatProvider, useChat } from "../components/AiBotUtils/ChatContext";

const DashboardContent = ({
  children,
  chatBotView,
  setChatBotView,
  chatOpen,
  setChatOpen,
}) => {
  const { messages, loading, sendMessage } = useChat();
  const { dashboardTheme } = useDashboardTheme();
  const [openSide, setOpenSide] = useState(false);
  const [splitSize, setSplitSize] = useState({
    horizontal: 40,
    vertical: 400,
  });

  const handleLayoutChange = (newView) => {
    setChatBotView(newView);
    if (newView !== "box") {
      setChatOpen(true);
    }
  };

  const handleCloseSplit = () => {
    setChatBotView("box");
    setChatOpen(false);
  };

  return (
    <ThemeProvider theme={dashboardTheme}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: openSide ? "72px 1fr" : "256px 1fr",
          gridTemplateRows: "auto 1fr",
          gridTemplateAreas: `"sidebar navbar" "sidebar content"`,
          minHeight: "100vh",
          minWidth: "100vw",
          overflow: "hidden",
          backgroundColor: "#f8fafc",
          transition: "grid-template-columns 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          willChange: "grid-template-columns",
          contain: "layout style",
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            gridArea: "sidebar",
            position: "relative",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          <SideNavBar openSide={openSide} setOpenSide={setOpenSide} />
        </Box>

        {/* Navbar */}
        <Box
          sx={{ gridArea: "navbar", position: "sticky", top: 0, zIndex: 100 }}
        >
          <Navbar />
        </Box>

        {/* Main Content Area */}
        <Box
          sx={{
            gridArea: "content",
            height: "calc(100vh - 64px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: chatBotView === "horizontalSplit" ? "column" : "row",
            position: "relative",
          }}
        >
          {/* Main Content */}
          <Box
            id="main-scroll-container"
            sx={{
              flex: chatBotView === "verticalSplit" ? 2 : 1,
              height:
                chatBotView === "horizontalSplit"
                  ? `${100 - splitSize.horizontal}%`
                  : "100%",
              width:
                chatBotView === "verticalSplit"
                  ? `calc(100% - ${splitSize.vertical}px)`
                  : "100%",
              overflow: "auto",
              scrollBehavior: "smooth",
              WebkitOverflowScrolling: "touch",
              backgroundImage: `
                radial-gradient(circle at 1px 1px, rgba(0,0,0,0.08) 1px, transparent 0),
                linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)
              `,
              backgroundSize: "20px 20px, 100% 100%",
              backgroundPosition: "0 0, 0 0",
              p: "1rem",
              transform: "translateZ(0)",
              willChange: "transform",
            }}
          >
            {children}
          </Box>

          {/* Full Screen Chat */}
          {chatOpen && chatBotView === "fullScreen" && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
                bgcolor: "background.paper",
              }}
            >
              <ChatBox
                messages={messages}
                loading={loading}
                onSubmit={sendMessage}
                onLayoutChange={handleLayoutChange}
                currentLayout={chatBotView}
                fullScreen
                onClose={() => {
                  setChatBotView("box");
                  setChatOpen(false);
                }}
              />
            </Box>
          )}

          {/* Vertical Split Chat */}
          {chatOpen && chatBotView === "verticalSplit" && (
            <Box
              sx={{
                width: splitSize.vertical,
                height: "100%",
                position: "relative",
                borderLeft: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: -4,
                  top: 0,
                  bottom: 0,
                  width: 8,
                  cursor: "col-resize",
                  zIndex: 10,
                  "&:hover": { bgcolor: "primary.main", opacity: 0.5 },
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startX = e.clientX;
                  const startWidth = splitSize.vertical;

                  const handleMouseMove = (moveEvent) => {
                    const delta = startX - moveEvent.clientX;
                    const newWidth = startWidth + delta;
                    setSplitSize((prev) => ({
                      ...prev,
                      vertical: Math.max(300, Math.min(newWidth, 800)),
                    }));
                  };

                  const handleMouseUp = () => {
                    document.removeEventListener("mousemove", handleMouseMove);
                    document.removeEventListener("mouseup", handleMouseUp);
                  };

                  document.addEventListener("mousemove", handleMouseMove);
                  document.addEventListener("mouseup", handleMouseUp);
                }}
              />
              <ChatBox
                messages={messages}
                loading={loading}
                onSubmit={sendMessage}
                onLayoutChange={handleLayoutChange}
                currentLayout={chatBotView}
                showLayoutControls
                style={{ height: "100%" }}
              />
              <IconButton
                size="small"
                onClick={handleCloseSplit}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 6,
                  zIndex: 20,
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <CloseRounded sx={{ fontSize: "1rem", color: "error" }} />
              </IconButton>
            </Box>
          )}

          {/* Horizontal Split Chat */}
          {chatOpen && chatBotView === "horizontalSplit" && (
            <Box
              sx={{
                width: "100%",
                height: splitSize.horizontal + "%",
                position: "relative",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  left: 0,
                  right: 0,
                  height: 8,
                  cursor: "row-resize",
                  zIndex: 10,
                  "&:hover": { bgcolor: "primary.main", opacity: 0.5 },
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startY = e.clientY;
                  const startHeight = splitSize.horizontal;

                  const handleMouseMove = (moveEvent) => {
                    const delta = startY - moveEvent.clientY;
                    const newHeight =
                      startHeight + (delta / window.innerHeight) * 100;
                    setSplitSize((prev) => ({
                      ...prev,
                      horizontal: Math.max(20, Math.min(newHeight, 80)),
                    }));
                  };

                  const handleMouseUp = () => {
                    document.removeEventListener("mousemove", handleMouseMove);
                    document.removeEventListener("mouseup", handleMouseUp);
                  };

                  document.addEventListener("mousemove", handleMouseMove);
                  document.addEventListener("mouseup", handleMouseUp);
                }}
              />
              <ChatBox
                messages={messages}
                loading={loading}
                onSubmit={sendMessage}
                onLayoutChange={handleLayoutChange}
                currentLayout={chatBotView}
                showLayoutControls
                style={{ height: "100%" }}
              />
              <IconButton
                size="small"
                onClick={handleCloseSplit}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 20,
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <CloseRounded sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

const DashboardLayout = ({ children }) => {
  const [chatBotView, setChatBotView] = useState("box");
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <DashboardThemeProvider>
      <ChatProvider>
        <DashboardContent
          children={children}
          chatBotView={chatBotView}
          setChatBotView={setChatBotView}
          chatOpen={chatOpen}
          setChatOpen={setChatOpen}
        />
        <ChatBotFabAnimated
          chatBotView={chatBotView}
          setChatBotView={setChatBotView}
          chatOpen={chatOpen}
          setChatOpen={setChatOpen}
        />
      </ChatProvider>
    </DashboardThemeProvider>
  );
};

export default DashboardLayout;
